<template>
  <section>
    <inline-alert v-if="!reports.length" :exclaim="true" type="danger">
      You do not have any reports available.
    </inline-alert>
    <div v-else>
      <div class="alert alert-warning">
        <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
        <strong>Note:</strong> Reports can take several minutes to complete and
        are processed in the background. Once complete, they will be dispatched
        to <strong> {{ deliveryEmail }} </strong>.
      </div>

      <div class="panel panel-primary mb-5">
        <div class="panel-heading">
          <strong>1.</strong> Select the type of report and date range
        </div>

        <div class="panel-body">
          <div class="form-group">
            <label for="report_option_type">Report Type</label>
            <select
              id="report_option_type"
              v-model="currentReportId"
              class="form-control"
              @change="reportTypeChanged()"
            >
              <option v-if="reports.length > 1" disabled :value="null">
                -- Select One --
              </option>
              <option
                v-for="report in reports"
                :key="`report-${report.id}`"
                :value="report.id"
              >
                {{ report.label }}
              </option>
            </select>
          </div>

          <!-- keys are assigned to each component so it re-renders, that way we can get a loaded action -->

          <report-option-date-range-component
            v-if="options.dateRange.enabled"
            :key="currentReportId + 'dateRange'"
            v-model="options.dateRange.values"
            @loaded="trackOptionLoaded()"
            @completed="trackOptionCompleted()"
          ></report-option-date-range-component>

          <report-option-month-year-component
            v-if="options.monthAndYear.enabled"
            :key="currentReportId + 'monthAndYear'"
            v-model="options.monthAndYear.values"
            @loaded="trackOptionLoaded()"
            @completed="trackOptionCompleted()"
          ></report-option-month-year-component>
        </div>
      </div>

      <div v-if="hasReportCustomizations" class="panel panel-primary mb-5">
        <div class="panel-heading">
          <strong>2.</strong> Customize the report
        </div>
        <div class="panel-body">
          <report-option-locale-component
            v-if="options.locale.enabled"
            :key="currentReportId + 'locale'"
            v-model="options.locale.values"
            :locales="locales"
            :all-allowed="options.localeIsOptional.enabled"
            @loaded="trackOptionLoaded()"
            @completed="trackOptionCompleted()"
          ></report-option-locale-component>

          <report-option-customer-segment-component
            v-if="options.customerSegment.enabled"
            :key="currentReportId + 'customerSegment'"
            v-model="options.customerSegment.values"
            :customer-segments="customerSegments"
            :all-allowed="options.customerSegmentIsOptional.enabled"
            @loaded="trackOptionLoaded()"
            @completed="trackOptionCompleted()"
          ></report-option-customer-segment-component>

          <report-option-var-component
            v-if="options.varId.enabled"
            :key="currentReportId + 'varId'"
            v-model="options.varId.values"
            :var-resellers="allowedVarResellers"
            :all-allowed="options.varIdIsOptional.enabled"
            @loaded="trackOptionLoaded()"
            @completed="trackOptionCompleted()"
          ></report-option-var-component>

          <report-option-status-component
              v-if="options.status.enabled"
              :key="currentReportId + 'status'"
              v-model="options.status.values"
              :statuses="statuses"
              :all-allowed="options.statusIsOptional.enabled"
              @loaded="trackOptionLoaded()"
              @completed="trackOptionCompleted()"
          >
          </report-option-status-component>
        </div>
      </div>

      <div class="panel panel-primary">
        <div class="panel-footer">
          <button
            ref="submit"
            class="btn btn-primary mr-4"
            :disabled="!canSubmit || submitting"
            @click="handleSubmit()"
          >
            Generate
          </button>
          <inline-loading-indicator
            v-if="submitting"
          ></inline-loading-indicator>
          <strong v-if="submissionErrorMessage" class="text-danger">
            {{ submissionErrorMessage }}
          </strong>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * Some thoughts on this architecture
 *
 * 1) Generally when you send in v-model into a child component, that comes in a property, and then you should emit changes. This is
 * what v-model is intended to do with input values. However, since we're sending in objects as v-models, a shortcut we're taking is
 * not having to duplicate them as data. This makes the child components much smaller.  A purist could argue that you should never
 * adjust 'props' even if they are deep models, and I recognize this.
 * 2) Another give and take of this approach, is that we get the benefit of defining the properties in the parent, so we know the
 * keys. However, the child doesn't get to explicitly define these properties - you just have to 'know'. If these child components
 * were re-used, this would make much more of a problem - these are only used here, so you can reasonably assume someone using them
 * has the context of the ExportComponent parent.
 */

import swal from "sweetalert2";

const reportOptionKeys = [
  "dateRange",
  "monthAndYear",
  "locale",
  "localeIsOptional",
  "customerSegment",
  "customerSegmentIsOptional",
  "varId",
  "varIdIsOptional",
  "status",
  "statusIsOptional",
];

export default {
  props: {
    deliveryEmail: {
      required: true,
      type: String,
    },

    reports: {
      required: true,
      type: Array,
    },

    lastMonthStartDate: {
      required: true,
      type: String,
    },

    lastMonthEndDate: {
      required: true,
      type: String,
    },

    lastMonthMonth: {
      required: true,
      type: String,
    },

    lastMonthYear: {
      required: true,
      type: String,
    },

    locales: {
      required: true,
      type: Array,
    },

    customerSegments: {
      required: true,
      type: Array,
    },

    varResellers: {
      required: true,
      type: Array,
    },
  },

  data() {
    return {
      submitting: false,
      submissionErrorMessage: "",

      optionsLoading: 0,
      incompleteOptions: 0,
      allowedVarResellers: this.varResellers,

      currentReportId: null,
      statuses: null,

      options: {
        dateRange: {
          enabled: false,
          values: {
            start: this.lastMonthStartDate,
            end: this.lastMonthEndDate,
          },
        },

        monthAndYear: {
          enabled: false,
          values: {
            // we're not sharing with dateStart and dateEnd because we don't want one adjustment to affect another drop down
            month: this.lastMonthMonth,
            year: this.lastMonthYear,
          },
        },

        locale: {
          enabled: false,
          values: {
            id: null,
          },
        },

        localeIsOptional: {
          enabled: false,
          supporting: true,
        },

        customerSegment: {
          enabled: false,
          values: {
            id: null,
          },
        },

        customerSegmentIsOptional: {
          enabled: false,
          supporting: true,
        },

        varId: {
          enabled: false,
          values: {
            id: null,
          },
        },

        varIdIsOptional: {
          enabled: false,
          supporting: true,
        },

        status: {
          enabled: false,
          values: {
            id: null,
          },
        },

        statusIsOptional: {
          enabled: false,
          supporting: true,
        },
      },
    };
  },

  computed: {
    canSubmit() {
      return (
        this.currentReportId !== null &&
        this.optionsLoading === 0 &&
        this.incompleteOptions === 0
      );
    },

    hasReportCustomizations() {
      const hasOneEnabled = [
        "varId",
        "locale",
        "customerSegment",
        "status",
      ];

      return hasOneEnabled.some((enabledKey) => {
        return this.options[enabledKey].enabled;
      });
    },
  },

  watch: {
    // eslint-disable-next-line func-names
    "options.locale.values.id": function () {
      this.getAllowedResellers();
    },

    // eslint-disable-next-line func-names
    "options.customerSegment.values.id": function () {
      this.getAllowedResellers();
    },
  },

  mounted() {
    if (this.reports.length === 1) {
      this.currentReportId = this.reports[0].id;
      this.reportTypeChanged();
      this.getAllowedResellers();
    }
  },

  methods: {
    reportTypeChanged() {
      /**
       * Every time we change the report type, reset the report options
       * Note: we do NOT change the values, just change the visibility
       */
      reportOptionKeys.forEach((option) => {
        this.options[option].enabled = false;
      });
      this.optionsLoading = 0;
      this.incompleteOptions = 0;
      this.submissionErrorMessage = "";

      const report = this.reports.find((r) => r.id === this.currentReportId);

      /**
       * After setting the options it has, we also increment a count for each option that could be loading
       */
      reportOptionKeys.forEach((option) => {
        this.options[option].enabled = Boolean(report[option]);
        if (this.options[option].enabled && !this.options[option].supporting) {
          this.optionsLoading += 1;
          this.incompleteOptions += 1;
        }
      });

      if (this.options.locale.enabled) {
        this.options.locale.values.id = null;
      }

      if (this.options.customerSegment.enabled) {
        this.options.customerSegment.values.id = null;
      }

      if (this.options.status.enabled) {
        this.options.status.values.id = null;
        this.statuses = report['statuses'];
      }

      this.getAllowedResellers();
    },

    getAllowedResellers() {
      let resellers = this.varResellers;

      if (this.options.varId.enabled) {
        if (
          this.options.locale.enabled &&
          this.options.locale.values.id !== "" &&
          this.options.locale.values.id !== null
        ) {
          resellers = resellers.filter(
            (reseller) => reseller.locale_id === this.options.locale.values.id
          );
        }

        if (
          this.options.customerSegment.enabled &&
          this.options.customerSegment.values.id !== "" &&
          this.options.customerSegment.values.id !== null
        ) {
          resellers = resellers.filter((reseller) =>
            reseller.customer_segments.some(
              (segment) =>
                segment.customer_segment_id ===
                this.options.customerSegment.values.id
            )
          );
        }
      }

      this.allowedVarResellers = resellers;
      this.options.varId.values.id = null;
    },

    trackOptionLoaded() {
      this.optionsLoading -= 1;
    },

    trackOptionCompleted() {
      this.incompleteOptions -= 1;
    },

    handleSubmit() {
      this.startSubmission();

      const postData = {
        reportId: this.currentReportId,
      };

      reportOptionKeys.forEach((option) => {
        if (this.options[option].enabled) {
          postData[option] = this.options[option].values;
        }
      });

      this.$http
        .post("/account/report", postData)
        .then(() => {
          this.endSubmission();
          return swal.fire(
            "Submitted!",
            "Your report has been queued and will be delivered shortly.",
            "success"
          );
        })
        .catch((e) => {
          if (e.response.status !== 422) {
            // eslint-disable-next-line
            alert("An unknown error occurred.");
            this.endSubmission();
            return;
          }

          // ideally, we'd want to pass through all the errors to individual components
          // but since this interface is going to be replaced at some point - plus - the
          // likeliness of someone to get an error is very small, we'll just combine all the
          // error messages instead.
          const errors = [
            [e.response.data.message],
            ...Object.values(e.response.data.errors),
          ];

          this.submissionErrorMessage = errors
            .flat()
            .reduce((m, error) => `${m} ${error}`);
          this.endSubmission();
        });
    },

    startSubmission() {
      this.submitting = true;
      this.submissionErrorMessage = "";
    },

    endSubmission() {
      this.submitting = false;
      this.$refs.submit.blur();
    },
  },
};
</script>
